import React, { useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Button,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Link as LinkIcon, Warning as WarningIcon } from '@mui/icons-material';
import { productsService } from '../services/api';
import { productVariantsService } from '../services/productVariantsService';
import priceScraperService from '../services/priceScraperService';

const CompetitorUrls = ({ urls, onChange, productId, variantId, type = 'product' }) => {
  const [newUrl, setNewUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [variantDialog, setVariantDialog] = useState({ open: false, url: '', variants: [], selectedVariant: null, selectedIndex: null });
  const [analyzing, setAnalyzing] = useState(false);

  const getCompetitorName = (url) => {
    try {
      const hostname = new URL(url).hostname;
      return hostname.replace('www.', '').split('.')[0];
    } catch {
      return 'Inconnu';
    }
  };

  const handleAddUrl = async () => {
    if (newUrl.trim() && isValidUrl(newUrl)) {
      // D'abord analyser l'URL pour détecter les variantes
      setAnalyzing(true);
      try {
        const analysis = await priceScraperService.analyzeUrl(newUrl.trim());
        
        if (analysis.analysis.hasMultiplePrices) {
          // Ouvrir le dialog de sélection des variantes
          setVariantDialog({
            open: true,
            url: newUrl.trim(),
            variants: analysis.analysis.prices,
            selectedVariant: null,
            selectedIndex: null
          });
          setNewUrl('');
          return;
        }
        
        // Pas de variantes multiples, ajouter directement
        await addUrlDirectly(newUrl.trim());
      } catch (error) {
        console.error('Erreur lors de l\'analyse:', error);
        // En cas d'erreur, ajouter quand même l'URL
        await addUrlDirectly(newUrl.trim());
      } finally {
        setAnalyzing(false);
      }
    }
  };

  const addUrlDirectly = async (url) => {
    const updatedUrls = [...urls, url];
    
    // Sauvegarder directement si on a un ID
    if (productId || variantId) {
      setSaving(true);
      try {
        const data = { competitorUrls: updatedUrls };
        
        if (type === 'product' && productId) {
          await productsService.update(productId, data);
        } else if (type === 'variant' && variantId) {
          await productVariantsService.update(variantId, data);
        }
        
        onChange(updatedUrls);
        setNewUrl('');
        setNotification({
          open: true,
          message: 'URL ajoutée et sauvegardée avec succès',
          severity: 'success'
        });
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error);
        setNotification({
          open: true,
          message: 'Erreur lors de la sauvegarde de l\'URL',
          severity: 'error'
        });
      } finally {
        setSaving(false);
      }
    } else {
      // Mode sans sauvegarde automatique (pour création)
      onChange(updatedUrls);
      setNewUrl('');
    }
  };

  const handleRemoveUrl = async (index) => {
    const updatedUrls = urls.filter((_, i) => i !== index);
    
    // Sauvegarder directement si on a un ID
    if (productId || variantId) {
      setSaving(true);
      try {
        const data = { competitorUrls: updatedUrls.length > 0 ? updatedUrls : null };
        
        if (type === 'product' && productId) {
          await productsService.update(productId, data);
        } else if (type === 'variant' && variantId) {
          await productVariantsService.update(variantId, data);
        }
        
        onChange(updatedUrls);
        setNotification({
          open: true,
          message: 'URL supprimée avec succès',
          severity: 'success'
        });
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error);
        setNotification({
          open: true,
          message: 'Erreur lors de la suppression de l\'URL',
          severity: 'error'
        });
      } finally {
        setSaving(false);
      }
    } else {
      // Mode sans sauvegarde automatique (pour création)
      onChange(updatedUrls);
    }
  };

  const handleVariantSelection = (variant, index) => {
    setVariantDialog(prev => ({ ...prev, selectedVariant: variant, selectedIndex: index }));
  };

  const handleConfirmVariant = async () => {
    if (variantDialog.selectedVariant && variantDialog.selectedIndex !== null) {
      // Ajouter l'URL avec l'information de la variante sélectionnée
      // On stocke l'index de la variante pour pouvoir la retrouver lors du scraping
      const urlWithVariant = {
        url: variantDialog.url,
        variantSelector: variantDialog.selectedIndex.toString(), // L'index de la variante
        variantPrice: variantDialog.selectedVariant.price,
        variantContext: variantDialog.selectedVariant.context
      };
      
      const updatedUrls = [...urls, urlWithVariant];
      
      // Sauvegarder si on a un ID
      if (productId || variantId) {
        setSaving(true);
        try {
          const data = { competitorUrls: updatedUrls };
          
          if (type === 'product' && productId) {
            await productsService.update(productId, data);
          } else if (type === 'variant' && variantId) {
            await productVariantsService.update(variantId, data);
          }
          
          onChange(updatedUrls);
          setNotification({
            open: true,
            message: `URL ajoutée avec la variante sélectionnée (${variantDialog.selectedVariant.price}€)`,
            severity: 'success'
          });
        } catch (error) {
          console.error('Erreur lors de la sauvegarde:', error);
          setNotification({
            open: true,
            message: 'Erreur lors de la sauvegarde de l\'URL',
            severity: 'error'
          });
        } finally {
          setSaving(false);
        }
      } else {
        onChange(updatedUrls);
      }
    }
    
    setVariantDialog({ open: false, url: '', variants: [], selectedVariant: null, selectedIndex: null });
  };

  const handleCancelVariant = () => {
    setVariantDialog({ open: false, url: '', variants: [], selectedVariant: null, selectedIndex: null });
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch {
      return false;
    }
  };

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          <LinkIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          URLs des concurrents
          {saving && <CircularProgress size={16} sx={{ ml: 2, verticalAlign: 'middle' }} />}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          {productId || variantId ? 
            'Les URLs sont sauvegardées automatiquement' : 
            'Ajoutez les URL des produits concurrents pour suivre leurs prix'
          }
        </Typography>

        <Box display="flex" gap={1} mb={2}>
          <TextField
            fullWidth
            size="small"
            placeholder="https://exemple.com/produit"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !saving && handleAddUrl()}
            error={newUrl && !isValidUrl(newUrl)}
            helperText={newUrl && !isValidUrl(newUrl) ? 'URL invalide' : ''}
            disabled={saving}
          />
          <Button
            variant="contained"
            onClick={handleAddUrl}
            disabled={!newUrl || !isValidUrl(newUrl) || saving || analyzing}
            startIcon={analyzing ? <CircularProgress size={20} /> : saving ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {analyzing ? 'Analyse...' : saving ? 'Enregistrement...' : 'Ajouter'}
          </Button>
        </Box>

      {urls && urls.length > 0 ? (
        <List dense>
          {urls.map((urlItem, index) => {
            const isUrlWithVariant = typeof urlItem === 'object' && urlItem.url;
            const url = isUrlWithVariant ? urlItem.url : urlItem;
            
            return (
              <ListItem
                key={index}
                sx={{
                  bgcolor: 'background.paper',
                  mb: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Chip
                        label={getCompetitorName(url)}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                        {url}
                      </Typography>
                      {isUrlWithVariant && urlItem.variantPrice && (
                        <Chip
                          label={`${urlItem.variantPrice}€`}
                          size="small"
                          color="success"
                          variant="filled"
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    isUrlWithVariant && urlItem.variantContext ? (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        Variante: {urlItem.variantContext.substring(0, 100)}...
                      </Typography>
                    ) : null
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton 
                    edge="end" 
                    onClick={() => handleRemoveUrl(index)} 
                    color="error"
                    disabled={saving}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary" align="center" py={2}>
          Aucune URL ajoutée
        </Typography>
      )}
      </Paper>

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setNotification({ ...notification, open: false })} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Dialog de sélection des variantes */}
      <Dialog 
        open={variantDialog.open} 
        onClose={handleCancelVariant}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            <Typography variant="h6">
              Variantes détectées
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Plusieurs variantes ont été détectées sur cette page. Veuillez choisir celle que vous souhaitez surveiller :
          </Typography>
          
          <FormControl component="fieldset" sx={{ mt: 2, width: '100%' }}>
            <FormLabel component="legend">Sélectionner une variante :</FormLabel>
            <RadioGroup
              value={variantDialog.selectedIndex !== null ? variantDialog.selectedIndex.toString() : ''}
              onChange={(e) => {
                const selectedIndex = parseInt(e.target.value);
                const selected = variantDialog.variants[selectedIndex];
                handleVariantSelection(selected, selectedIndex);
              }}
            >
              {variantDialog.variants.map((variant, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <FormControlLabel
                    value={index.toString()}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          {variant.price}€
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Sélecteur: {variant.selector}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Contexte: {variant.context.substring(0, 150)}...
                        </Typography>
                      </Box>
                    }
                  />
                  {index < variantDialog.variants.length - 1 && <Divider sx={{ mt: 1 }} />}
                </Box>
              ))}
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelVariant} color="inherit">
            Annuler
          </Button>
          <Button 
            onClick={handleConfirmVariant} 
            variant="contained"
            disabled={!variantDialog.selectedVariant || saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? 'Enregistrement...' : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompetitorUrls;

